import * as React from "react";
import equal from "fast-deep-equal";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { SearchTime } from "../../components/molecules";
import { ITherapistAvailability } from "../../services/api/therapists";
import { IRootState } from "../../store/index";
import { actionCreators as therapistsActions } from "../../store/therapists/actions";
import { availableDateTime } from "../../utils/availableDateTime";
import { dateFromIso } from "../../utils/dateIso";

interface IState {
  message: string;
  selectedTime?: string;
  timeItems: string[];
}

interface IProps {
  onClick(justclose?: boolean): void;
  trackChange(value: string | undefined): void;
}

interface IReduxProps {
  availableDates: ITherapistAvailability;
  isAvailableDatesLoading: boolean;
  selectedDate?: string;
}

type RouteProps = RouteComponentProps<{ categoryName: string }>;
type Props = IProps & IReduxProps & RouteProps & { dispatch: Dispatch<any> };

class SearchTimeContainer extends React.Component<Props, IState> {
  // check if string is an ISO date or a simple date (2022-08-08)
  private doesDateHaveTime = (date) => {
    return date.includes("T");
  };

  private getSelectedTime = (date: string) => {
    if (this.props.selectedDate && this.doesDateHaveTime(date)) {
      return dateFromIso(date).toFormat("h:mm a");
    }

    return;
  };

  public state: IState = {
    message: "",
    selectedTime: this.getSelectedTime(this.props.selectedDate),
    timeItems: [],
  };

  /**
   * Map available time slots for selected date
   */
  private mapTime() {
    if (
      !Object.keys(this.props.availableDates).length ||
      !Object.keys(this.props.availableDates)[0].length ||
      !this.props.selectedDate
    ) {
      return;
    }

    const available = availableDateTime(this.props.selectedDate);

    if (typeof this.props.availableDates[available.date] === "undefined") {
      this.setState({
        message: "No time",
        selectedTime: undefined,
        timeItems: [],
      });

      return;
    }

    const timeItems = this.props.availableDates[available.date].map((time) => {
      return dateFromIso(time).toFormat("h:mm a");
    });

    this.setState({ timeItems });
  }

  public componentDidMount() {
    if (this.props.availableDates) {
      this.mapTime();
    }
  }

  public componentDidUpdate(prevProps: Props) {
    if (!equal(prevProps.availableDates, this.props.availableDates)) {
      this.setState({ message: "", selectedTime: undefined });
      this.mapTime();
    }

    if (
      prevProps.selectedDate !== this.props.selectedDate &&
      !this.props.selectedDate
    ) {
      this.setState({ timeItems: [], selectedTime: undefined });
    }
  }

  public selectTime = (time: string) => {
    const PM = 12;
    const [hour, min] = time.split(":");
    const minutes = parseInt(min, 10);
    const hours = parseInt(hour, 10);
    const hours24 = min.includes("PM") && hours < PM ? hours + PM : hours;
    const dateTime = dateFromIso(this.props.selectedDate).set({
      hour: hours24,
      minute: minutes,
    });

    this.setState({ selectedTime: dateTime.toFormat("h:mm a") });
    this.props.dispatch(therapistsActions.setDate(dateTime.toISO()));
  };

  public onDateClick = () => {
    if (!this.props.selectedDate) {
      this.props.onClick();
    } else {
      this.props.onClick(true);
    }
  };

  /**
   * Render time selector
   */
  public render() {
    return (
      <SearchTime
        items={this.state.timeItems}
        selectedTime={this.state.selectedTime}
        placeholder={
          this.state.message
            ? this.state.message
            : this.props.isAvailableDatesLoading
            ? "Loading..."
            : "Time"
        }
        onClick={this.onDateClick}
        onSelect={this.selectTime}
        trackChange={this.props.trackChange}
      />
    );
  }
}

// store
const mapStateToProps = (state: IRootState): IReduxProps => {
  return {
    availableDates: state.therapistsState.selectedTherapistAvailableDates,
    isAvailableDatesLoading: state.therapistsState.isAvailableDatesLoading,
    selectedDate: state.therapistsState.selectedDate,
  };
};

const store = withRouter(
  connect<IReduxProps, {}, {}, IRootState>(mapStateToProps)(
    SearchTimeContainer,
  ),
);

export { store as SearchTimeContainer };
